import { useEffect, useRef, useState } from "react";
import { searchCards } from "./functions";

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

/**
 * Busca assistida de carta no TCGdex. Não guarda estado de vínculo: só devolve a carta
 * escolhida em onSelect({ ...card, lang }); quem chama decide o que fazer.
 * "trocar" desfaz a escolha e avisa o pai em onClear() — sem isso o pai continuaria
 * achando que há carta escolhida.
 */
const CardSearchField = ({ token, lang = "pt", onSelect, onClear, selected = null, disabled = false }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [chosen, setChosen] = useState(selected);
    const timer = useRef(null);
    const seqRef = useRef(0);
    const aliveRef = useRef(true);

    useEffect(() => setChosen(selected), [selected]);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        const term = q.trim();
        if (term.length < MIN_CHARS) {
            setResults([]);
            setError("");
            setLoading(false);
            return undefined;
        }
        const mySeq = ++seqRef.current;
        timer.current = setTimeout(async () => {
            setLoading(true);
            setError("");
            const out = await searchCards(token, { q: term, lang });
            if (!aliveRef.current || seqRef.current !== mySeq) return; // resposta obsoleta ou componente desmontado
            setLoading(false);
            if (out?.type === "success") {
                setResults(Array.isArray(out.data) ? out.data : []);
            } else {
                setResults([]);
                setError(out?.message || "Falha na busca");
            }
        }, DEBOUNCE_MS);
        return () => timer.current && clearTimeout(timer.current);
    }, [q, lang, token]);

    const choose = (card) => {
        if (disabled) return;
        const payload = { ...card, lang };
        setChosen(payload);
        setResults([]);
        setQ("");
        if (onSelect) onSelect(payload);
    };

    const clear = () => {
        setChosen(null);
        if (onClear) onClear();
    };

    return (
        <div className="text-left">
            {chosen ? (
                <div className="flex items-center gap-2 border border-[#d59106] p-1 text-sm">
                    {chosen.thumb ? <img src={chosen.thumb} alt={chosen.name} className="h-10" /> : null}
                    <span className="flex-1">
                        <strong>{chosen.name}</strong> · {chosen.setName || chosen.setId} #{chosen.localId} · <code>{chosen.cardId}</code>
                    </span>
                    {!disabled ? (
                        <button type="button" className="text-[#d59106] underline" onClick={clear}>trocar</button>
                    ) : null}
                </div>
            ) : (
                <>
                    <input
                        type="text"
                        value={q}
                        disabled={disabled}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar carta no TCGdex (nome da carta)"
                        className="w-full text-black px-2"
                    />
                    {loading ? <p className="text-xs text-gray-400">Buscando…</p> : null}
                    {error ? <p className="text-xs text-red-400">{error}</p> : null}
                    {results.length ? (
                        <ul className="max-h-60 overflow-y-auto border border-[#b47c06] bg-[#1a1a1a]" role="listbox" aria-label="Resultados">
                            {results.map((card) => (
                                <li
                                    key={card.cardId}
                                    role="option"
                                    tabIndex={0}
                                    aria-selected={false}
                                    className="flex items-center gap-2 p-1 cursor-pointer hover:bg-[#2a2a2a]"
                                    onClick={() => choose(card)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            choose(card);
                                        }
                                    }}
                                >
                                    {card.thumb ? <img src={card.thumb} alt={card.name} className="h-12" /> : <span className="h-12 w-8 bg-gray-700 inline-block" />}
                                    <span className="text-sm">
                                        {card.name} <span className="text-gray-400">· {card.setName || card.setId} #{card.localId}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default CardSearchField;
